import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { statusMeta } from "../lib/status";
import { toast } from "sonner";
import { Plus, Search, FolderOpen, Trash2, FileText, Clock, Scale } from "lucide-react";

export default function Dashboard() {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [clientName, setClientName] = useState("");
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const res = await api.get("/cases");
      setCases(res.data);
    } catch (e) {
      toast.error("Falha ao carregar casos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createCase = async () => {
    if (!title.trim()) { toast.error("Informe um título para o caso"); return; }
    setCreating(true);
    try {
      const res = await api.post("/cases", { title: title.trim(), client_name: clientName.trim() });
      toast.success("Caso criado");
      setOpen(false); setTitle(""); setClientName("");
      navigate(`/case/${res.data.id}`);
    } catch (e) {
      toast.error("Falha ao criar caso");
    } finally {
      setCreating(false);
    }
  };

  const removeCase = async (id) => {
    try {
      await api.delete(`/cases/${id}`);
      toast.success("Caso excluído");
      setCases((c) => c.filter((x) => x.id !== id));
    } catch (e) {
      toast.error("Falha ao excluir");
    }
  };

  const filtered = cases.filter(
    (c) =>
      c.title?.toLowerCase().includes(query.toLowerCase()) ||
      c.client_name?.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-amber-600 mb-1">Painel de Casos</p>
            <h1 className="font-display text-3xl font-extrabold tracking-tight">Meus Casos Trabalhistas</h1>
            <p className="text-muted-foreground text-sm mt-1">Gerencie atendimentos, documentos e petições iniciais.</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-case-btn" className="bg-slate-900 hover:bg-slate-800 text-white gap-2 h-11 px-5">
                <Plus className="h-4 w-4" /> Novo Caso
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Novo caso</DialogTitle>
                <DialogDescription>Crie um caso e faça upload da ficha de atendimento e anexos.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label htmlFor="title">Título do caso *</Label>
                  <Input data-testid="case-title-input" id="title" value={title}
                    onChange={(e) => setTitle(e.target.value)} placeholder="Ex.: Reclamação — João da Silva x Empresa XYZ" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="client">Nome do reclamante</Label>
                  <Input data-testid="case-client-input" id="client" value={clientName}
                    onChange={(e) => setClientName(e.target.value)} placeholder="Ex.: João da Silva" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button data-testid="create-case-submit" onClick={createCase} disabled={creating}
                  className="bg-slate-900 hover:bg-slate-800 text-white">
                  {creating ? "Criando…" : "Criar caso"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <div className="relative mb-6 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="case-search-input" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por título ou reclamante…" className="pl-10 h-11 bg-white" />
        </div>

        {loading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => <div key={i} className="h-40 rounded-xl bg-muted animate-pulse" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="border border-dashed rounded-2xl py-20 text-center bg-white">
            <div className="h-14 w-14 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto mb-4">
              <Scale className="h-7 w-7 text-amber-600" />
            </div>
            <h3 className="font-display text-lg font-bold">Nenhum caso ainda</h3>
            <p className="text-muted-foreground text-sm mt-1 mb-5">Crie seu primeiro caso para começar.</p>
            <Button data-testid="empty-new-case-btn" onClick={() => setOpen(true)}
              className="bg-slate-900 hover:bg-slate-800 text-white gap-2">
              <Plus className="h-4 w-4" /> Novo Caso
            </Button>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="cases-grid">
            {filtered.map((c) => {
              const sm = statusMeta(c.status);
              return (
                <div key={c.id} data-testid={`case-card-${c.id}`}
                  className="group bg-white border border-slate-200 rounded-xl p-5 hover:shadow-lg hover:border-slate-300 transition-all">
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <Badge variant="outline" className={`${sm.className} border text-[11px] font-semibold`}>{sm.label}</Badge>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button data-testid={`delete-case-${c.id}`}
                          className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-rose-600 transition">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Excluir caso?</AlertDialogTitle>
                          <AlertDialogDescription>Esta ação não pode ser desfeita. O caso "{c.title}" será removido.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction onClick={() => removeCase(c.id)} className="bg-rose-600 hover:bg-rose-700">Excluir</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                  <button onClick={() => navigate(`/case/${c.id}`)} className="text-left w-full">
                    <h3 className="font-display font-bold text-base leading-snug line-clamp-2 mb-1 group-hover:text-amber-700 transition">{c.title}</h3>
                    {c.client_name && <p className="text-sm text-muted-foreground mb-3">{c.client_name}</p>}
                    <div className="flex items-center gap-4 text-xs text-muted-foreground mt-3 pt-3 border-t">
                      <span className="flex items-center gap-1"><FileText className="h-3.5 w-3.5" />{(c.claims || []).length} pedidos</span>
                      <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{new Date(c.updated_at).toLocaleDateString("pt-BR")}</span>
                    </div>
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
